// Pure logic for the watch inbox: which sequence step each message is, the
// filter chips, the list order, and keeping their images from loading.

// labelMessages maps each message id to what the server worked out it is:
// a step in a sequence, a one-off blast, or not sorted (with why).
export function labelMessages(flows) {
  const labels = new Map();
  ((flows && flows.flows) || []).forEach((flow) => {
    const steps = flow.steps || [];
    steps.forEach((step) => {
      (step.message_ids || []).forEach((id) => labels.set(id, {
        kind: "step",
        group: flow.group,
        flowLabel: flow.label,
        step: step.step_order + 1,
        steps: steps.length,
        offsetMinutes: step.offset_minutes,
        delayMinutes: step.delay_minutes,
      }));
    });
  });
  ((flows && flows.broadcasts) || []).forEach((cluster) => {
    (cluster.message_ids || []).forEach((id) => labels.set(id, { kind: "blast", sentOn: cluster.sent_on }));
  });
  ((flows && flows.unclassified) || []).forEach((cluster) => {
    (cluster.message_ids || []).forEach((id) => labels.set(id, { kind: "unsorted", reason: cluster.reason }));
  });
  return labels;
}

function labelOf(labels, message) {
  return labels.get(message.id) || { kind: "unsorted" };
}

// Set when an email can't be sorted only because a signup hasn't got mail yet.
function waitingFor(label, waiting) {
  return waiting && label && label.kind === "unsorted" && /only one address/.test(label.reason || "") ? waiting : null;
}

// inboxFilters: All, one chip per sequence, then Blasts and Not sorted. Empty
// ones are left out, except All. waiting is the signup mail is waiting on, which
// names the chip when that's the reason for all of them.
export function inboxFilters(flows, messages, labels, waiting) {
  const count = (test) => (messages || []).filter((m) => test(labelOf(labels, m))).length;
  const chips = [{ key: "all", label: "All", count: (messages || []).length }];
  ((flows && flows.flows) || []).forEach((flow) => {
    // A watch has one drip, and its label is just the competitor's name again.
    chips.push({ key: `flow:${flow.group}`, label: "Drip", group: flow.group,
      count: count((l) => l.kind === "step" && l.group === flow.group) });
  });
  chips.push({ key: "blasts", label: "Blasts", count: count((l) => l.kind === "blast") });
  const unsorted = count((l) => l.kind === "unsorted");
  const allWaiting = unsorted > 0 && count((l) => !!waitingFor(l, waiting)) === unsorted;
  chips.push({ key: "unsorted", label: allWaiting ? `Waiting on signup ${waiting}` : "Not sorted", count: unsorted, waiting: allWaiting });
  return chips.filter((c) => c.key === "all" || c.count > 0);
}

function sentTime(message) {
  return Date.parse(message.sent_date || message.received_date) || 0;
}

// filterMessages returns the list to show. A sequence reads in step order (the
// same step reaches each address, oldest first); everything else newest first.
// addressId, when set, keeps only mail sent to that address.
export function filterMessages(messages, labels, filter, addressId) {
  let out = (messages || []).filter((m) => !addressId || m.address_id === addressId);
  if (filter && filter.startsWith("flow:")) {
    const group = filter.slice(5);
    out = out.filter((m) => {
      const l = labelOf(labels, m);
      return l.kind === "step" && l.group === group;
    });
    return out.sort((a, b) => labelOf(labels, a).step - labelOf(labels, b).step || sentTime(a) - sentTime(b));
  }
  if (filter === "blasts") out = out.filter((m) => labelOf(labels, m).kind === "blast");
  if (filter === "unsorted") out = out.filter((m) => labelOf(labels, m).kind === "unsorted");
  return out.sort((a, b) => sentTime(b) - sentTime(a));
}

// rowTag is the short tag on each list row.
export function rowTag(label, waiting) {
  if (waitingFor(label, waiting)) return { text: `Waiting on signup ${waiting}`, color: "gray" };
  if (!label || label.kind === "unsorted") return { text: "Not sorted", color: "gray" };
  if (label.kind === "blast") return { text: "Blast", color: "purple" };
  return { text: `Step ${label.step}`, color: "arcoblue" };
}

// step moves the selection up (-1) or down (+1), staying in the list.
export function step(list, selectedId, by) {
  if (!list.length) return null;
  const at = list.findIndex((m) => m.id === selectedId);
  if (at < 0) return list[0].id;
  return list[Math.min(list.length - 1, Math.max(0, at + by))].id;
}

// While images are off, the email may load nothing from the web, since any
// image, stylesheet or font it fetches tells the sender it was opened. Its
// own inline styles and embedded (data:) images still show.
const BLOCK_REMOTE = "default-src 'none'; style-src 'unsafe-inline'; img-src data: cid:; font-src data:";

// blockRemote adds a content rule to the email that stops it loading anything
// from the web. It goes first in the head so it covers everything after it.
// Before the doctype would switch the page to old-browser layout, so it never
// goes there.
export function blockRemote(html) {
  const meta = `<meta http-equiv="Content-Security-Policy" content="${BLOCK_REMOTE}">`;
  const source = html || "";
  for (const tag of [/<head\b[^>]*>/i, /<html\b[^>]*>/i, /<!doctype[^>]*>/i]) {
    const m = source.match(tag);
    if (m) {
      const end = m.index + m[0].length;
      return source.slice(0, end) + meta + source.slice(end);
    }
  }
  return meta + source;
}

// The images choice is remembered per watch, in this browser only.
const imagesKey = (watchId) => `spy-images:${watchId}`;

export function loadShowImages(watchId) {
  try {
    return window.localStorage.getItem(imagesKey(watchId)) === "on";
  } catch (e) {
    return false;
  }
}

export function saveShowImages(watchId, on) {
  try {
    if (on) window.localStorage.setItem(imagesKey(watchId), "on");
    else window.localStorage.removeItem(imagesKey(watchId));
  } catch (e) {
    // private window or blocked storage: the choice lasts until reload
  }
}

// isUnread: nobody has opened this email in the app yet.
export function isUnread(message) {
  return !!message && !message.viewed_date;
}

// setViewed returns the messages with one marked read (a date) or unread
// (null). The others are the same objects.
export function setViewed(messages, id, viewedDate) {
  return (messages || []).map((m) => (m.id === id ? { ...m, viewed_date: viewedDate } : m));
}
